import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

/* ---------- 3D-tilt browser frame ---------- */
function TiltFrame({ children, url, accent = "blue" }) {
  const ref = useRef(null);

  // Mouse positions (-0.5 to 0.5)
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 150, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 150, damping: 18 });
  // shine position
  const shineX = useTransform(mx, [-0.5, 0.5], ["0%", "100%"]);

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
      className="browser-frame w-full will-change-transform"
      data-testid="browser-mockup"
    >
      <div className="flex items-center gap-2 px-4 h-9 border-b border-white/[0.06] bg-white/[0.02]">
        <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
        <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
        <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
        <div className="flex-1 flex justify-center">
          <div className="px-3 py-1 rounded-md bg-white/[0.04] border border-white/[0.05] text-[11px] font-mono text-zinc-500">
            {url}
          </div>
        </div>
        <span
          className={`w-2 h-2 rounded-full ${
            accent === "amber"
              ? "bg-amber-400/70"
              : accent === "emerald"
              ? "bg-emerald-400/70"
              : "bg-blue-400/70"
          } animate-pulse`}
        />
      </div>

      <div className="relative aspect-[16/10] overflow-hidden">
        {children}
        {/* Cursor-following shine */}
        <motion.div
          style={{
            background: `radial-gradient(400px circle at ${shineX} 30%, rgba(255,255,255,0.10), transparent 60%)`,
          }}
          className="absolute inset-0 pointer-events-none mix-blend-screen"
        />
      </div>
    </motion.div>
  );
}

/* ---------- Animated demo content ---------- */
function TischlereiMockup() {
  return (
    <div className="absolute inset-0 bg-[#0a0a0b]">
      <motion.img
        initial={{ scale: 1.08 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: EASE }}
        src="https://images.unsplash.com/photo-1590880795696-20c7dfadacde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxjYXJwZW50ZXIlMjB3b3Jrc2hvcCUyMG1vZGVybnxlbnwwfHx8fDE3Nzg4NzE2OTV8MA&ixlib=rb-4.1.0&q=85"
        alt="Tischlerei in Jena"
        className="absolute inset-0 w-full h-full object-cover opacity-70"
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#050505] via-[#050505]/40 to-transparent" />
      <div className="absolute inset-0 p-8 flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <span className="font-display text-sm tracking-tight text-white">
            Tischlerei<span className="text-zinc-500"> · Jena</span>
          </span>
          <div className="hidden sm:flex items-center gap-4 text-[11px] text-zinc-300/80">
            <span>Leistungen</span>
            <span>Projekte</span>
            <span>Kontakt</span>
          </div>
        </div>
        <div className="max-w-sm">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">Konzept · Handwerk</div>
          <div className="mt-2 font-display text-2xl sm:text-3xl leading-tight text-white">
            Handwerk, das man sehen kann.
          </div>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-black text-[11px] font-medium">
            Termin vereinbaren <ArrowUpRight className="w-3 h-3" />
          </div>
        </div>
      </div>
    </div>
  );
}

function BaeckereiMockup() {
  return (
    <div className="absolute inset-0 bg-[#0a0a0b]">
      <motion.img
        initial={{ scale: 1.08 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: EASE }}
        src="https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=1600&q=80"
        alt="Bäckerei in Weimar"
        className="absolute inset-0 w-full h-full object-cover opacity-65"
      />
      <div className="absolute inset-0 bg-gradient-to-bl from-amber-500/[0.08] via-transparent to-[#050505]" />
      <div className="absolute inset-0 p-8 flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <span className="font-display text-sm tracking-tight text-white">
            Bäckerei<span className="text-zinc-500"> · Weimar</span>
          </span>
          <span className="text-[10px] font-mono text-amber-200/70">geöffnet · Mo–Sa</span>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-200/80">Konzept · Gastronomie</div>
          <div className="mt-2 font-display text-2xl sm:text-3xl leading-tight text-white max-w-md">
            Frisches Brot. Jeden Morgen, aus Weimar.
          </div>
          <div className="mt-4 flex gap-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-black text-[11px] font-medium">
              Sortiment <ArrowUpRight className="w-3 h-3" />
            </div>
            <div className="inline-flex items-center px-3 py-1.5 rounded-full border border-white/15 text-white text-[11px]">
              Öffnungszeiten
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const items = [
  {
    tag: "Konzept · Handwerk",
    title: "Tischlerei in Jena",
    url: "tischlerei-jena.example",
    desc: "Eine ruhige, materialbetonte Website mit Fokus auf Vertrauen, Referenzen und schneller Kontaktaufnahme. Klare Bildsprache, lokales SEO und einfache Pflege.",
    Mock: TischlereiMockup,
    meta: ["Brand & Webdesign", "Hosting", "Lokales SEO"],
  },
  {
    tag: "Konzept · Gastronomie",
    title: "Bäckerei in Weimar",
    url: "baeckerei-weimar.example",
    desc: "Ein warmer, einladender Look mit klaren Öffnungszeiten, Sortiment und Standortinfo. Mobile-first gedacht, schnell und ohne Ballast.",
    Mock: BaeckereiMockup,
    meta: ["Frontend-Erlebnis", "Standort & Kontakt", "Performance"],
    accent: "amber",
  },
];

/* ---------- Section ---------- */
export default function Showcase() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const glowY = useTransform(scrollYProgress, [0, 1], [-80, 80]);

  return (
    <section
      id="arbeiten"
      ref={ref}
      data-testid="showcase-section"
      className="relative py-24 sm:py-32 border-t border-white/[0.05] overflow-hidden"
    >
      <motion.div
        style={{ y: glowY }}
        className="absolute top-1/2 -translate-y-1/2 left-0 w-[600px] h-[600px] glow-blue opacity-40 pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-20 max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-block text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-semibold"
          >
            ↳ Ausgewählte Arbeiten
          </motion.span>

          <h2 className="mt-3 font-display text-3xl sm:text-5xl text-white tracking-tight leading-[1.05]">
            <span className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                whileInView={{ y: "0%" }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, ease: EASE }}
                className="inline-block"
              >
                Demos, die zeigen, wie modernes
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                whileInView={{ y: "0%" }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, delay: 0.1, ease: EASE }}
                className="inline-block"
              >
                Web heute aussieht.
              </motion.span>
            </span>
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="mt-5 text-base text-zinc-400 leading-relaxed"
          >
            Zwei kuratierte Konzept-Websites, die unsere Designsprache und
            unser technisches Niveau zeigen — kein Stockmaterial, sondern echte
            Frontend-Arbeit.
          </motion.p>
        </div>

        <div className="space-y-28">
          {items.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: EASE }}
              data-testid={`showcase-item-${i}`}
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-7">
                <TiltFrame url={p.url} accent={p.accent || "blue"}>
                  <p.Mock />
                </TiltFrame>
              </div>
              <div className="lg:col-span-5">
                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                >
                  <div className="text-[11px] uppercase tracking-[0.2em] text-blue-300/80">
                    {p.tag}
                  </div>
                  <h3 className="mt-3 font-display text-2xl sm:text-3xl text-white tracking-tight leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-zinc-400 text-base leading-relaxed">{p.desc}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {p.meta.map((m) => (
                      <span
                        key={m}
                        className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 text-zinc-300 bg-white/[0.02]"
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
